import React, { useState, useEffect } from "react";
import { View, StyleSheet } from "react-native";
import {
  TextInput,
  Avatar,
  Text,
  Button,
  useTheme,
  Title,
  HelperText,
  Portal,
  Dialog,
  Paragraph,
} from "react-native-paper";
//import FileIcon from "./file-icon";
import { getDocumentAsync, DocumentResult } from "expo-document-picker";
import { useFileMutations } from "../util/api";
import { useValidationState } from "../util/validation";
import v8n from "v8n";
import Modal from "./modal-fullpage";
import { useLocation, LocationStatus } from "../contexts/location";
// import { Oval } from "react-loader-spinner";

function UploadModal({
  visible,
  setVisible,
}: {
  visible: boolean;
  setVisible: (visibility: boolean) => void;
}) {
  const theme = useTheme();
  const styles = useStyles();
  const [doc, setDoc] = useState<Extract<
    DocumentResult,
    { type: "success" }
  > | null>(null);
  const [doc_name, onChangeName] = useValidationState<string>(
    "",
    v8n().string().maxLength(50)
  );
  const [desc, onChangeDesc] = useValidationState<string>(
    "", // Empty string to show placeholder
    v8n().string().minLength(1).maxLength(400) // Require at least 1 character
  );
  const { fetchLocation, locationState } = useLocation();
  const { addFileMutation } = useFileMutations();
  
  // Error dialog state
  const [errorDialogVisible, setErrorDialogVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const showError = (message: string) => {
    setErrorMessage(message);
    setErrorDialogVisible(true);
  };

  const hideError = () => {
    setErrorDialogVisible(false);
    setErrorMessage("");
  };

  // Resets State on Component Reload
  useEffect(() => {
    if (!visible) {
      setDoc(null);
      onChangeDesc("");
      onChangeName("");
    }
  }, [visible]);

  useEffect(() => {
    fetchLocation();
  }, []);

  const pickDoc = async () => {
    try {
      // What files do we want to limit to be picked
      const res = await getDocumentAsync({
        multiple: false,
        type: "*/*", // Changed from empty string to accept all files
      });
      console.log("Document picker result:", res); // Add logging
      
      // Handle the different response formats between web and native
      if (res.canceled === false && res.assets && res.assets.length > 0) {
        // New expo-document-picker format (v11+)
        const asset = res.assets[0];
        console.log("File selected (new format):", asset);
        setDoc({
          type: "success",
          name: asset.name,
          size: asset.size,
          uri: asset.uri,
          mimeType: asset.mimeType,
        } as Extract<DocumentResult, { type: "success" }>);
        if (doc_name.value === "") {
          onChangeName(asset.name);
        }
      } else if (res.type === "success") {
        // Old format fallback
        console.log("File selected (old format):", res.name);
        setDoc(res);
        if (doc_name.value === "") {
          onChangeName(res.name);
        }
      } else if (res.type === "cancel" || res.canceled === true) {
        console.log("User cancelled file picker");
      }
    } catch (err) {
      console.error("Error picking document:", err);
    }
  };

  const uploadDoc = async () => {
    if (locationState.status != LocationStatus.LOCATION_READY) {
      console.error("Location not ready:", locationState.status);
      showError("Location not available. Please enable location services and refresh the page.");
      return;
    }
    if (doc == null) {
      console.error("No document selected");
      showError("Please select a file first.");
      return;
    }
    if (!doc_name.ok || !desc.ok) {
      console.error("Validation failed", { doc_name: doc_name.ok, desc: desc.ok });
      showError("Please fill in all required fields correctly.");
      return;
    }
    if (!addFileMutation.isLoading) {
      console.log("Uploading file:", {
        doc_name: doc_name.value,
        desc: desc.value,
        location: locationState.location,
        file: doc,
      });
      addFileMutation.mutate(
        {
          doc: doc,
          desc: desc.value || "No description", // Fallback if somehow empty
          doc_name: doc_name.value,
          loc: locationState.location,
        },
        {
          onSuccess: () => {
            console.log("Upload successful!");
            setVisible(false);
          },
          onError: (error: any) => {
            console.error("Upload failed:", error);
            console.error("Error response:", error.response?.data);
            showError(error.response?.data?.error || error.message || 'Network error. Please check your connection and try again.');
          },
        }
      );
    } else {
      console.log("Upload in progress ...");
    }
  };

  /*
    <FileIcon
      style={styles.fileIcon}
      ext={doc ? doc.name.split(".").pop() : "file"}
    />
   */
  return (
    <React.Fragment>
      <Modal visible={visible}>
        <View style={styles.form}>
          <Title style={styles.modalText}>Upload</Title>
          <Avatar.Icon
            icon="file"
            color={theme.colors.text}
            style={styles.fileIcon}
            size={300}
          />
          <Text numberOfLines={1} style={styles.fileName}>
            {doc ? doc.name : ""}
          </Text>
          <Button
            icon="paperclip"
            style={styles.buttonStyle}
            onPress={() => pickDoc()}
            mode="contained"
          >
            Browse
          </Button>
          {doc ? (
            <>
              <View style={styles.formText}>
                <TextInput
                  mode="flat"
                  style={styles.formTextInput}
                  placeholder="Name"
                  value={doc_name.value}
                  error={!doc_name.ok}
                  onChangeText={onChangeName}
                  maxLength={50}
                />
                <HelperText type="error" visible={!doc_name.ok}>
                  {!doc_name.ok ? "Length too long" : ""}
                </HelperText>
                <TextInput
                  multiline={true}
                  mode="flat"
                  style={styles.formTextInput}
                  placeholder="Description"
                  value={desc.value}
                  error={!desc.ok}
                  onChangeText={onChangeDesc}
                  maxLength={400}
                />
                <HelperText
                  type="error"
                  visible={!desc.ok}
                  style={{ opacity: 1 }}
                >
                  {!desc.ok && desc.value.length === 0 ? "Required" : !desc.ok ? "Length too long" : ""}
                </HelperText>
              </View>
              <Button
                icon="check"
                style={styles.buttonStyle}
                onPress={() => uploadDoc()}
                loading={addFileMutation.isLoading}
                mode="contained"
              >
                Confirm
              </Button>
            </>
          ) : null}
          <Button
            icon="cancel"
            style={styles.buttonStyle}
            onPress={() => setVisible(false)}
            mode="contained"
          >
            Cancel
          </Button>
        </View>
      </Modal>
      
      {/* Error Dialog */}
      <Portal>
        <Dialog 
          visible={errorDialogVisible} 
          onDismiss={hideError}
          style={{ maxWidth: 400, alignSelf: "center" }}
        >
          <Dialog.Icon icon="alert-circle" size={48} />
          <Dialog.Title style={{ textAlign: "center" }}>Upload Error</Dialog.Title>
          <Dialog.Content>
            <Paragraph>{errorMessage}</Paragraph>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={hideError}>OK</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </React.Fragment>
  );
}

const useStyles = () => {
  const theme = useTheme();
  const styles = StyleSheet.create({
    formTextInput: {
      backgroundColor: theme.colors.surface,
      margin: 5,
    },
    modalText: {
      fontSize: 54,
      fontWeight: "900",
      fontStyle: "italic",
      textAlign: "center",
      color: theme.colors.text,
    },
    form: {
      width: "100%",
      padding: 20,
      elevation: 3,
      alignItems: "center",
    },
    filePreview: {
      backgroundColor: "rgb(27,27,27)",
      borderRadius: 5,
      color: "white",
      shadowColor: "#000",
      shadowOffset: {
        width: 0,
        height: 0,
      },
      shadowOpacity: 0.25,
      shadowRadius: 3.84,
      padding: 20,
      justifyContent: "space-between",
    },
    fileName: {
      marginTop: -50,
      marginBottom: 30,
      fontSize: 20,
      fontStyle: "italic",
      fontWeight: "400",
    },
    fileIcon: {
      backgroundColor: "rgba(0,0,0,0)",
    },
    rowFlexCenter: {
      flexDirection: "row",
      alignItems: "center",
      flex: 1,
    },
    formText: {
      marginTop: 20,
      marginBottom: 20,
    },
    buttonStyle: {
      marginVertical: 10,
    },
  });
  return styles;
};

export default UploadModal;
